import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import * as moment from 'moment';
import SwalComponent from './../../utils/custom-swal.component';

import { FormService } from './../../api/form.service';
import { TransactionService } from '../../api/transaction.service';
import uuid from '../../utils/uuid';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
@Component({
  selector: 'iv-lease-incentive',
  templateUrl: './lease-incentive.component.html',
  styleUrls: ['./lease-incentive.component.scss']
})
export class LeaseIncentiveComponent implements OnInit, OnDestroy {
  @Input() index = 0;
  @Input() incentive;
  @Input() disabled = false;
  @Input() getUserCountry;
  @Output() saveIncentiveEvent;
  @Output() deleteIncentiveEvent;
  @ViewChild('warnDateValidAlert', { static: true }) warnDateValidAlert: SwalComponent;

  incentiveTypes = [];
  uuid = uuid();
  incentiveForm;
  swalOptions: any;

  $incentiveFormChanges;
  incentiveType$: Subscription;
  incentiveAmount$: Subscription;
  description$: Subscription;
  noofmonths$: Subscription;
  dateApplied$: Subscription;

  constructor(
    private fb: UntypedFormBuilder,
    private formService: FormService,
    private transactionService: TransactionService) {
    this.saveIncentiveEvent = new EventEmitter();
    this.deleteIncentiveEvent = new EventEmitter();
    this.swalOptions = this.getSwalOptions();
  }

  ngOnInit() {
    // Getting field values
    this.formService.getFieldOptions('incentive', 'type').subscribe(incentivetype => {
      this.incentiveTypes = incentivetype;
    });
    this.setupForm();
  }
  setupForm() {
    this.incentiveForm = this.fb.group({
      incentive_type: [{ value: this.incentive.incentive_type || null, disabled: this.disabled }, Validators.required],
      incentive_amount: [{ value: this.incentive.incentive_amount || '0.00', disabled: this.disabled }],
      description: [{ value: this.incentive.description || null, disabled: this.disabled }, Validators.required],
      no_of_months: [{ value: this.incentive.no_of_months || null, disabled: this.disabled }],
      date_applied: [this.incentive.date_applied ? moment(this.incentive.date_applied) : null],
    });
    this.formService.addLoadedForm(this.incentiveForm, this.uuid);
    this.setupRentReviewSubscription();
  }

  setupRentReviewSubscription() {
    this.unsubscribeEvents();
    this.incentiveType$ = this.incentiveForm.controls.
      incentive_type.valueChanges
      .pipe(
        debounceTime(850),
        distinctUntilChanged()
      )
      .subscribe(changes => {
        let incentiveTypeChanges;
        if (changes) {
          this.incentive.incentive_type = changes;
          incentiveTypeChanges = this.patchFields(changes, this.incentive);
        }
        changes = {
          ...this.incentive,
          ...{ 'incentive_type': changes },
          ...incentiveTypeChanges,
          'id': this.incentive.id
        };
        this.saveIncentiveEvent.emit(changes);
      });
    this.incentiveAmount$ = this.incentiveForm.controls.
      incentive_amount.valueChanges
      .pipe(
        debounceTime(850),
        distinctUntilChanged()
      )
      .subscribe(changes => {
        changes = {
          ...this.incentive,
          ... { 'incentive_amount': changes },
          'id': this.incentive.id
        };
        this.saveIncentiveEvent.emit(changes);
      });
    this.description$ = this.incentiveForm.controls.
      description.valueChanges
      .pipe(
        debounceTime(850),
        distinctUntilChanged()
      )
      .subscribe(changes => {
        changes = {
          ...this.incentive,
          ... { 'description': changes },
          'id': this.incentive.id
        };
        this.saveIncentiveEvent.emit(changes);
      });
    this.noofmonths$ = this.incentiveForm.controls.
      no_of_months.valueChanges
      .pipe(
        debounceTime(850),
        distinctUntilChanged()
      )
      .subscribe(changes => {
        changes = {
          ...this.incentive,
          ... { 'no_of_months': changes },
          'id': this.incentive.id
        };
        this.saveIncentiveEvent.emit(changes);
      });
    this.dateApplied$ = this.incentiveForm.controls.
      date_applied.valueChanges
      .pipe(
        debounceTime(850),
        distinctUntilChanged()
      )
      .subscribe(changes => {
        if (changes) {
          changes = moment(changes).format('YYYY-MM-DD');
        }
        if (changes !== 'Invalid date') {
          changes = {
            ...this.incentive,
            ... { 'date_applied': changes },
            'id': this.incentive.id
          };
          this.saveIncentiveEvent.emit(changes);
        }
      });
  }

  showFitOutOptions() {
    return this.incentiveForm.get('incentive_type').value === 'fit_out' ? true : false;
  }

  showFreeRentOptions() {
    return this.incentiveForm.get('incentive_type').value === 'free_rent' ? true : false;
  }

  showOtherOptions() {
    return this.incentiveForm.get('incentive_type').value === 'other' ? true : false;
  }

  deleteIncentive(incentive) {
    this.deleteIncentiveEvent.emit(incentive);
  }

  validateDate(event) {
    if (event && event.targetElement && event.targetElement.value) {
      let sDate;
      let regexp;
      regexp = this.transactionService.dateValidator(this.getUserCountry);
      if (regexp.test(event.targetElement.value)) {
        sDate = this.transactionService.dateConvertor(this.getUserCountry, event.targetElement.value, 'lease-incentive');
        this.incentiveForm.patchValue({
          'date_applied': sDate
        }, { emitEvent: false });
        sDate = moment(event.targetElement.value).format('YYYY-DD-MM');
        const changes = {
          ...this.incentive,
          ... { 'date_applied': sDate },
          'id': this.incentive.id
        };
        if (this.incentive.date_applied !== 'Invalid date') {
          this.saveIncentiveEvent.emit(changes);
        }
      } if (!regexp.test(event.targetElement.value)) {
        this.warnDateValidAlert.text = 'Invalid Date';
        this.warnDateValidAlert.fire().then(response => {
          if (response.value || response.dismiss) {
            this.incentiveForm.patchValue({
              'date_applied': null
            });
          }
        });
      }
    }
  }

  patchFields(incentiveType, changes) {
    switch (incentiveType) {
      case 'fit_out':
        this.incentiveForm.patchValue({
          'no_of_months': null,
          'description': null
        }, { emitEvent: false });
        changes.no_of_months = null;
        changes.description = null;
        break;
      case 'free_rent':
        this.incentiveForm.patchValue({
          'date_applied': null,
          'description': null
        }, { emitEvent: false });
        changes.date_applied = null;
        changes.description = null;
        break;
      case 'other':
        this.incentiveForm.patchValue({
          'date_applied': null,
          'no_of_months': null
        }, { emitEvent: false });
        changes.no_of_months = null;
        changes.date_applied = null;
        break;
      default:
        this.incentiveForm.patchValue({
          'date_applied': null,
          'no_of_months': null,
          'description': null
        }, { emitEvent: false });
        changes.no_of_months = null;
        changes.date_applied = null;
        changes.description = null;
        break;
    }
    return changes;
  }
  unsubscribeEvents() {
    if (this.incentiveType$) {
      this.incentiveType$.unsubscribe();
    }
    if (this.description$) {
      this.description$.unsubscribe();
    }
    if (this.incentiveAmount$) {
      this.incentiveAmount$.unsubscribe();
    }
    if (this.noofmonths$) {
      this.noofmonths$.unsubscribe();
    }
    if (this.dateApplied$) {
      this.dateApplied$.unsubscribe();
    }
  }
  getSwalOptions() {
    // {showConfirmButton: true, confirmButtonText: 'Continue', showCancelButton: true, showCloseButton: true}
    const options = {
      showConfirmButton: true,
      showCloseButton: true,
      confirmButtonText: 'Continue',
      cancelButtonText: 'Cancel',
      showCancelButton: true
    };
    return this.formService.getSwalOptions(options);
  }
  ngOnDestroy() {
    this.unsubscribeEvents();
  }

}
